import React from 'react';
import { Trophy, ExternalLink } from 'lucide-react';
import { DraftSummaryPick } from '../../types';
import PickBadge from '../common/PickBadge';

interface DraftSummaryStripProps {
  draftPicks: DraftSummaryPick[];
  onPlayerClick?: (playerId: number) => void;
  onViewDraftBoard?: () => void;
}

/**
 * DraftSummaryStrip - Responsive grid draft picks display
 * Features:
 * - Responsive grid layout (3-6 columns based on screen size)
 * - Draft pick cards showing R<x> P<y> • Name (Pos)
 * - Click to jump to player in roster view
 * - Link to full draft board
 * - Color-coded by position
 * - No horizontal scrolling required
 */
const DraftSummaryStrip: React.FC<DraftSummaryStripProps> = ({
  draftPicks,
  onPlayerClick,
  onViewDraftBoard
}) => {
  const getPositionColor = (position: string) => {
    const colors: { [key: string]: string } = {
      'QB': 'bg-purple-900/30 text-purple-400 border-purple-700',
      'RB': 'bg-green-900/30 text-green-400 border-green-700',
      'WR': 'bg-blue-900/30 text-blue-400 border-blue-700',
      'TE': 'bg-yellow-900/30 text-yellow-400 border-yellow-700',
      'K': 'bg-orange-900/30 text-orange-400 border-orange-700',
      'D/ST': 'bg-red-900/30 text-red-400 border-red-700',
      'DEF': 'bg-red-900/30 text-red-400 border-red-700'
    };
    return colors[position] || 'bg-slate-900/30 text-slate-400 border-slate-700';
  };

  if (draftPicks.length === 0) {
    return (
      <div className="bg-background-secondary border border-fieldline rounded-lg p-6 text-center">
        <Trophy className="w-8 h-8 text-text-tertiary mx-auto mb-3" />
        <div className="text-text-secondary">No draft picks found</div>
      </div>
    );
  }

  return (
    <div className="bg-background-secondary border border-fieldline rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary flex items-center space-x-2">
          <Trophy className="w-5 h-5 text-accent-warning" />
          <span>Draft Summary</span>
          <span className="text-text-secondary text-sm">({draftPicks.length} picks)</span>
        </h3>
        
        <div className="flex items-center space-x-4">
          {/* Legend */}
          <div className="flex items-center space-x-2">
            <PickBadge variant="boom" label="Boom" />
            <PickBadge variant="bust" label="Bust" />
          </div>
          
          {onViewDraftBoard && (
            <button
              onClick={onViewDraftBoard}
              className="flex items-center space-x-2 text-accent-primary hover:text-accent-primary/80 text-sm transition-colors"
            >
              <span>View Full Draft Board</span>
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Responsive Grid Draft Picks */}
      <div className="grid grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
        {draftPicks.map((pick) => (
          <button
            key={pick.player_id}
            onClick={() => onPlayerClick?.(pick.player_id)}
            className={`px-4 py-3 rounded-lg border transition-all duration-200 hover:scale-105 hover:shadow-lg ${getPositionColor(pick.position)}`}
            title={`Round ${pick.round}, Pick ${pick.pick} overall`}
          >
            <div className="text-center">
              <div className="font-bold text-sm mb-1">
                R{pick.round} • P{pick.pick}
              </div>
              <div className="font-medium flex items-center justify-center space-x-1">
                <span>{pick.player_name}</span>
                {pick.is_best_pick && <PickBadge variant="boom" className="ml-1" />}
                {pick.is_worst_pick && <PickBadge variant="bust" className="ml-1" />}
              </div>
              <div className="text-xs opacity-75">
                {pick.position}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Draft Stats Summary */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-fieldline">
        {(() => {
          const positionCounts = draftPicks.reduce((acc, pick) => {
            acc[pick.position] = (acc[pick.position] || 0) + 1;
            return acc;
          }, {} as Record<string, number>);

          return Object.entries(positionCounts).map(([position, count]) => (
            <div key={position} className="text-center">
              <div className={`text-lg font-bold ${getPositionColor(position).split(' ')[1]}`}>
                {count}
              </div>
              <div className="text-text-secondary text-sm">{position}</div>
            </div>
          ));
        })()}
      </div>
    </div>
  );
};

export default DraftSummaryStrip;
